import { Button, Form, Select, Space } from "antd";
import { useEffect } from "react";
import FilterIcon from "../../../assets/icons/FilterIcon";
import SelectDownIcon from "../../../assets/icons/SelectDownIcon";
import { useGetOfficeSettingDataQuery } from "../../../features/attendanceSetting/attendanceSettingApi";
import { useGetDepartmentsQuery } from "../../../features/department/departmentApi";
import { useGetWorkStationsQuery } from "../../../features/workStation/workStationApi";
import useError from "../../../hooks/useError";
import { DailyAttendanceFilterType } from "../../../pages/admin/Attendance/DailyAttendance";
import { ModalComponentProps } from "../../../types";
import {
  selectArrayOptionWithAll,
  selectSearchOption,
} from "../../../utils/utils";
import ModalCustomTitle from "../../common/ModalCustomTitle";
import CustomModal from "../../ui/CustomModal";

interface FilterModalProps extends ModalComponentProps {
  filter?: Omit<DailyAttendanceFilterType, "date">;
  setFilter: (values: DailyAttendanceFilterType) => void;
}

export default function FilterModal({
  handleClose,
  filter,
  setFilter,
  ...rest
}: FilterModalProps) {
  const [form] = Form.useForm();
  const {
    isLoading: isDepartmentLoading,
    isError: isDepartmentError,
    error: departmentError,
    data: departmentData,
  } = useGetDepartmentsQuery({
    params: { skip: 0, limit: 0, is_published: 1 },
  });
  const {
    isLoading: isWorkStationLoading,
    isError: isWorkStationError,
    error: workStationError,
    data: workStationData,
  } = useGetWorkStationsQuery({
    params: { skip: 0, limit: 0, is_published: 1 },
  });
  const {
    isLoading: isOfficeSettingLoading,
    isError: isOfficeSettingError,
    error: officeSettingError,
    data: officeSettingData,
  } = useGetOfficeSettingDataQuery({});
  useError(isDepartmentError, departmentError, "Get department data fail");
  useError(isOfficeSettingError, officeSettingError, "Get office data fail");
  useError(isWorkStationError, workStationError, "Work station get fail");

  const locationList =
    officeSettingData?.data?.office_settings_operational
      ?.business_office_locations;
  // const remoteList =
  //   officeSettingData?.data?.office_settings_operational?.attendance_eligibility?.remote_business_member_ids;
  const wifiList =
    officeSettingData?.data?.office_settings_operational?.business_offices;

  const onFinish = (values: DailyAttendanceFilterType) => {
    setFilter({ ...filter, ...values });
    handleClose();
  };

  useEffect(() => {
    form.setFieldsValue(filter);
  }, [filter, form]);

  const checkin_office_or_remote = Form.useWatch(
    "checkin_office_or_remote",
    form
  ) as DailyAttendanceFilterType["checkin_office_or_remote"];

  const checkout_office_or_remote = Form.useWatch(
    "checkout_office_or_remote",
    form
  ) as DailyAttendanceFilterType["checkout_office_or_remote"];

  return (
    <CustomModal
      footer={false}
      handleClose={handleClose}
      width={580}
      closeIcon={null}
      {...rest}
    >
      <ModalCustomTitle
        title="Filter"
        icon={<FilterIcon width={24} height={24} />}
        iconClassName="!bg-[#f9fafb] disabled:!text-body-text"
        handleClose={handleClose}
      />

      <Form
        className="custom-form"
        layout="vertical"
        requiredMark="optional"
        size="large"
        onFinish={onFinish}
        form={form}
      >
        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4">
          <Form.Item name="department_id" className="mb-4">
            <Select
              placeholder="Department"
              size="middle"
              allowClear
              showSearch
              filterOption={selectSearchOption}
              options={selectArrayOptionWithAll(
                departmentData?.data?.departments || [],
                "name",
                "id"
              )}
              suffixIcon={<SelectDownIcon />}
              loading={isDepartmentLoading}
            />
          </Form.Item>
          <Form.Item name="business_work_station_id" className="mb-4">
            <Select
              placeholder="Work Station"
              size="middle"
              allowClear
              showSearch
              filterOption={selectSearchOption}
              options={selectArrayOptionWithAll(
                workStationData?.data?.workStations || [],
                "name",
                "id"
              )}
              suffixIcon={<SelectDownIcon />}
              loading={isWorkStationLoading}
            />
          </Form.Item>
          <Form.Item name="status" className="mb-4">
            <Select
              placeholder="Status"
              size="middle"
              allowClear
              showSearch
              filterOption={selectSearchOption}
              options={status}
              suffixIcon={<SelectDownIcon />}
            />
          </Form.Item>
          <Form.Item name="checkin_status" className="mb-4">
            <Select
              placeholder="Check In"
              size="middle"
              allowClear
              showSearch
              filterOption={selectSearchOption}
              options={checkinStatus}
              suffixIcon={<SelectDownIcon />}
            />
          </Form.Item>
          <Form.Item
            name="checkin_office_or_remote"
            // initialValue="office"
            className="mb-4"
          >
            <Select
              allowClear
              placeholder="Checkin From"
              size="middle"
              showSearch
              filterOption={selectSearchOption}
              options={office_or_remote}
              suffixIcon={<SelectDownIcon />}
              onChange={() => {
                form.setFieldsValue({ checkin_location_id: undefined });
              }}
            />
          </Form.Item>
          {checkin_office_or_remote === "office" && (
            <Form.Item name="checkin_location_id" className="mb-4">
              <Select
                placeholder="Location for check in"
                size="middle"
                allowClear
                showSearch
                filterOption={selectSearchOption}
                options={selectArrayOptionWithAll(
                  locationList || [],
                  "location_name",
                  "id"
                )}
                suffixIcon={<SelectDownIcon />}
                loading={isOfficeSettingLoading}
              />
            </Form.Item>
          )}
          {/* {checkin_office_or_remote === "remote" && (
            <Form.Item name="checkin_location_id" className="mb-4">
              <Select
                placeholder="Wifi for check in"
                size="middle"
                allowClear
                showSearch
                filterOption={selectSearchOption}
                options={selectArrayOption(
                  remoteList || [],
                  "office_name",
                  "id"
                )}
                suffixIcon={<SelectDownIcon />}
                loading={isOfficeSettingLoading}
              />
            </Form.Item>
          )} */}
          {checkin_office_or_remote === "wifi" && (
            <Form.Item name="checkin_location_id" className="mb-4">
              <Select
                placeholder="Wifi for check in"
                size="middle"
                allowClear
                showSearch
                filterOption={selectSearchOption}
                options={selectArrayOptionWithAll(
                  wifiList || [],
                  "office_name",
                  "id"
                )}
                suffixIcon={<SelectDownIcon />}
                loading={isOfficeSettingLoading}
              />
            </Form.Item>
          )}

          <Form.Item name="checkout_status" className="mb-4">
            <Select
              placeholder="Check Out"
              size="middle"
              allowClear
              showSearch
              filterOption={selectSearchOption}
              options={checkoutStatus}
              suffixIcon={<SelectDownIcon />}
            />
          </Form.Item>

          <Form.Item
            name="checkout_office_or_remote"
            // initialValue="office"
            className="mb-4"
          >
            <Select
              allowClear
              placeholder="Checkout From"
              size="middle"
              showSearch
              filterOption={selectSearchOption}
              options={office_or_remote}
              suffixIcon={<SelectDownIcon />}
              onChange={() => {
                form.setFieldsValue({ checkout_location_id: undefined });
              }}
            />
          </Form.Item>
          {checkout_office_or_remote === "office" && (
            <Form.Item name="checkout_location_id" className="mb-4">
              <Select
                placeholder="Location for check out"
                size="middle"
                allowClear
                showSearch
                filterOption={selectSearchOption}
                options={selectArrayOptionWithAll(
                  locationList || [],
                  "location_name",
                  "id"
                )}
                suffixIcon={<SelectDownIcon />}
                loading={isOfficeSettingLoading}
              />
            </Form.Item>
          )}
          {checkout_office_or_remote === "wifi" && (
            <Form.Item name="checkout_location_id" className="mb-4">
              <Select
                placeholder="Wifi for check out"
                size="middle"
                allowClear
                showSearch
                filterOption={selectSearchOption}
                options={selectArrayOptionWithAll(
                  wifiList || [],
                  "office_name",
                  "id"
                )}
                suffixIcon={<SelectDownIcon />}
                loading={isOfficeSettingLoading}
              />
            </Form.Item>
          )}
        </div>

        <Space className="w-full mt-2" size={12}>
          <Button
            className="normal-dark-button modal-button"
            onClick={handleClose}
            size="middle"
          >
            Cancel
          </Button>
          <Button
            className="modal-button"
            type="primary"
            htmlType="submit"
            size="middle"
          >
            Filter
          </Button>
        </Space>
      </Form>
    </CustomModal>
  );
}

const status = [
  {
    value: "all",
    label: "All",
  },
  {
    value: "absent",
    label: "Absent",
  },
  {
    value: "present",
    label: "Present",
  },
  {
    value: "on_leave",
    label: "On Leave",
  },
];

const checkinStatus = [
  {
    value: "all",
    label: "All",
  },
  {
    value: "late",
    label: "Late",
  },
  {
    value: "on_time",
    label: "On Time",
  },
];

const checkoutStatus = [
  {
    value: "all",
    label: "All",
  },
  {
    value: "left_timely",
    label: "Left Timely",
  },
  {
    value: "left_early",
    label: "Left Early",
  },
];

const office_or_remote = [
  {
    value: "all",
    label: "All",
  },
  {
    value: "office",
    label: "Office",
  },
  {
    value: "remote",
    label: "Remote",
  },
  {
    value: "wifi",
    label: "Wifi",
  },
];
